/** biome-ignore-all lint/suspicious/useAwait: <explanation> */
import { hasValue, sortBy } from '@vrplatform/utils';
import { parse } from 'papaparse';
import type { CsvRowError } from './_types';
import type { ZodSchema } from 'zod';

type BasicRow = Record<string, string | null | number | boolean>;

type CsvParsingResult<TRow extends BasicRow> =
  | {
      success: false;
      errors: CsvRowError[];
    }
  | {
      success: true;
      data: TRow[];
    };

export const parseCsv = async <TRow extends BasicRow>({
  fileData,
  schema: schemaOrSchemaFn,
}: {
  fileData: string;
  schema: ZodSchema | ((input: TRow) => ZodSchema);
}): Promise<CsvParsingResult<TRow>> => {
  const result = parse<TRow>(fileData, {
    header: true,
    skipEmptyLines: true,
    // dynamicTyping: true,
  });

  if (result.errors.length > 0) {
    return {
      success: false,
      errors: result.errors.map((error) => ({
        rowNumber: error.row || 0,
        message: error.message,
      })),
    };
  }

  const row = result.data.at(0);

  const schema =
    typeof schemaOrSchemaFn === 'function'
      ? schemaOrSchemaFn(row!)
      : schemaOrSchemaFn;

  const errors: CsvRowError[] = [];

  // Parsing headers
  if (!result.meta.fields?.length)
    return {
      success: false,
      errors: [{ rowNumber: 0, message: 'No columns found' }],
    };

  // Forcing us to define all columns..
  /*result.meta.fields?.forEach((field) => {
    if (!columns.includes(field as any)) {
      errors.unshift({ rowNumber: 0, message: `Invalid column: ${field}` });
    }
  });*/

  /*const defaultKeys = (() => {
    if ((schema._def as { typeName: string }).typeName !== 'ZodObject') {
      const { schema: _schema = undefined } = (
        schema as any as {
          _def: {
            schema: {
              _def: {
                options: { shape: any }[];
              };
            };
          };
        }
      )._def;

      return uniqueBy(
        _schema?._def.options.reduce<string[]>((acc, curr) => {
          acc.push(...Object.keys(curr.shape));

          return acc;
        }, []) || []
      );
    }

    return Object.keys((schema as any).shape);
  })();

  defaultKeys.forEach((column) => {
    if (!result.meta.fields?.includes(column as string)) {
      errors.unshift({
        rowNumber: 0,
        message: `Missing column: ${column as string}`,
      });
    }
  });*/
  // Already return if columns are invalid
  if (errors.length)
    return { success: false, errors: sortBy(errors, 'rowNumber', 'asc') };

  // Parsing rows
  if (!result.data) {
    errors.push({ rowNumber: 1, message: 'No rows found' });
  } else {
    result.data.forEach((row, index) => {
      errors.push(
        ...parseCsvRow({
          row,
          rowNumber: index + 1,
          schema,
        })
      );
    });
  }

  if (errors.length)
    return { success: false, errors: sortBy(errors, 'rowNumber', 'asc') };

  return {
    success: true,
    data: result.data,
  };
};

export const parseCsvRow = <TRow extends BasicRow>({
  row,
  rowNumber,
  schema,
}: {
  row: TRow;
  rowNumber: number;
  schema: ZodSchema;
}): CsvRowError[] => {
  const errors: CsvRowError[] = [];

  const res = schema.safeParse(row);
  if (res.success === false) {
    const formatted = res.error.format();

    let messages: string[] = [];

    // Main props
    if (formatted._errors.length) {
      messages.push(...formatted._errors);
    }

    // Child properties
    Object.entries(formatted).forEach(([key, value]) => {
      if (key === '_errors') return;
      const mes = (value as any)?._errors?.[0];
      if (mes) messages.push(mes);
    });

    messages = messages.filter(hasValue);

    if (messages.length)
      errors.push(...messages.map((message) => ({ rowNumber, message })));
  }

  return errors;
};
